import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startDateInput", "endDateInput", "errorMessage"]

  connect() {
    this.currentPeriodController = null
    this.startFlatpickr = null
    this.endFlatpickr = null

    // Initialize flatpickr instances after a short delay to ensure they're mounted
    setTimeout(() => this.initFlatpickr(), 100)
  }

  initFlatpickr() {
    const startInput = this.startDateInputTarget
    const endInput = this.endDateInputTarget

    if (startInput._flatpickr) {
      this.startFlatpickr = startInput._flatpickr
    }
    if (endInput._flatpickr) {
      this.endFlatpickr = endInput._flatpickr
    }
  }

  open(periodController) {
    this.currentPeriodController = periodController

    // Get current values from the period controller
    const startDate = periodController.startDateHiddenTarget.value
    const endDate = periodController.endDateHiddenTarget.value
    const minDate = periodController.minDateValue
    const maxDate = periodController.maxDateValue

    // Set values in modal inputs
    this.startDateInputTarget.value = startDate
    this.endDateInputTarget.value = endDate

    // Update flatpickr instances with min/max dates
    if (this.startFlatpickr) {
      this.startFlatpickr.set('minDate', minDate)
      this.startFlatpickr.set('maxDate', maxDate)
      this.startFlatpickr.setDate(startDate, false)
    }
    if (this.endFlatpickr) {
      this.endFlatpickr.set('minDate', minDate)
      this.endFlatpickr.set('maxDate', maxDate)
      this.endFlatpickr.setDate(endDate, false)
    }

    // Hide error message
    this.hideError()

    // Show modal
    this.element.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
  }

  closeModal() {
    this.element.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    this.currentPeriodController = null
  }

  savePeriod() {
    const startDate = this.startDateInputTarget.value
    const endDate = this.endDateInputTarget.value

    // Validation: inputs cannot be empty
    if (!startDate || !endDate) {
      this.showError(this.getTranslation('empty_dates'))
      return
    }

    // Validation: end date must be greater than start date
    const startDateObj = new Date(startDate)
    const endDateObj = new Date(endDate)

    if (endDateObj <= startDateObj) {
      this.showError(this.getTranslation('invalid_dates'))
      return
    }

    // Update the hidden inputs and display text
    if (this.currentPeriodController) {
      this.currentPeriodController.startDateHiddenTarget.value = startDate
      this.currentPeriodController.endDateHiddenTarget.value = endDate

      // Format dates for display (dd/mm/yyyy)
      const formattedStart = this.formatDateForDisplay(startDateObj)
      const formattedEnd = this.formatDateForDisplay(endDateObj)
      this.currentPeriodController.displayTextTarget.textContent = `${formattedStart} - ${formattedEnd}`
    }

    this.closeModal()
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.classList.remove('hidden')
  }

  hideError() {
    this.errorMessageTarget.classList.add('hidden')
    this.errorMessageTarget.textContent = ''
  }

  formatDateForDisplay(date) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  getTranslation(key) {
    const translations = {
      empty_dates: {
        fr: 'Les deux dates sont obligatoires',
        en: 'Both dates are required'
      },
      invalid_dates: {
        fr: 'La date de fin doit être supérieure à la date de début',
        en: 'End date must be greater than start date'
      }
    }

    // Try to detect locale from URL or document
    const locale = document.documentElement.lang ||
                   (window.location.pathname.match(/^\/(fr|en)\//) || [])[1] ||
                   'fr'

    return translations[key]?.[locale] || translations[key]?.fr || key
  }

  // Handle escape key
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeModal()
    }
  }
}
